// ============================================================================
// appContextMenu.ts — ЕДИНЫЙ SSoT контекстного меню приложения (ПКМ).
// Браузерное меню в приложении НЕ показывается: глобальный перехват
// contextmenu (AppContextMenu) подавляет его и открывает НАШЕ меню.
// Локальные меню (дерево конфигурации, формы) работают как раньше — они
// вызывают e.preventDefault() сами, глобальный обработчик уважает
// defaultPrevented (SRP: один глобальный слой, локальные — расширения).
// ============================================================================

import { create } from 'zustand';
import { createLogger } from '@/lib/logger';
import { isWailsMode } from '@/lib/wailsBridge';

const log = createLogger('ctxmenu');

export interface AppMenuItem {
  id: string;
  label: string;
  /** Разделитель ПЕРЕД пунктом. */
  separatorBefore?: boolean;
  disabled?: boolean;
  danger?: boolean;
  action: () => void;
}

interface CtxState {
  open: boolean;
  x: number;
  y: number;
  items: AppMenuItem[];
  openMenu: (x: number, y: number, items: AppMenuItem[]) => void;
  closeMenu: () => void;
}

export const useAppContextMenu = create<CtxState>((set) => ({
  open: false,
  x: 0,
  y: 0,
  items: [],
  openMenu: (x, y, items) => set({ open: true, x, y, items }),
  closeMenu: () => set({ open: false, items: [] }),
}));

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || t.isContentEditable;
}

/** Пункты по умолчанию: буфер обмена (по контексту) + обновление (web/PWA). */
export function defaultContextItems(target: EventTarget | null): AppMenuItem[] {
  const items: AppMenuItem[] = [];
  const editable = isEditableTarget(target);
  const sel = window.getSelection()?.toString() || '';

  if (editable) {
    items.push(
      {
        id: 'cut',
        label: 'Вырезать',
        disabled: !sel,
        action: () => {
          navigator.clipboard.writeText(sel).then(() => document.execCommand('delete')).catch((e) => log.warn('вырезать: ' + String(e)));
        },
      },
      {
        id: 'copy',
        label: 'Копировать',
        disabled: !sel,
        action: () => {
          navigator.clipboard.writeText(sel).catch((e) => log.warn('копировать: ' + String(e)));
        },
      },
      {
        id: 'paste',
        label: 'Вставить',
        action: () => {
          navigator.clipboard
            .readText()
            .then((text) => document.execCommand('insertText', false, text))
            .catch((e) => log.warn('вставить: ' + String(e)));
        },
      },
      { id: 'selectAll', label: 'Выделить всё', separatorBefore: true, action: () => document.execCommand('selectAll') },
    );
  } else if (sel) {
    items.push({
      id: 'copy',
      label: 'Копировать',
      action: () => {
        navigator.clipboard.writeText(sel).catch((e) => log.warn('копировать: ' + String(e)));
      },
    });
  }

  // Обновление страницы: в web/PWA безопасно; в Wails reload рвёт runtime.
  if (!isWailsMode()) {
    items.push({
      id: 'reload',
      label: 'Обновить страницу',
      separatorBefore: items.length > 0,
      action: () => window.location.reload(),
    });
  }
  return items;
}
